import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType, Navigate } from 'react-router-dom';
import usePresencePageReport from './hooks/usePresencePageReport'; // [2026-09-22] 현재 페이지를 유저 API 에 알림(대시보드 접속자 현황)
import DOMPurify from 'dompurify'; // XSS 방지를 위한 HTML 정제 라이브러리
import { AuthProvider } from './hooks/useAuth';
import useDomainTheme from './hooks/useDomainTheme';
import useDomainPermission from './hooks/useDomainPermission';
import { DesignTemplateProvider } from './contexts/DesignTemplateContext';
// [2026-09-27] 사이트 알림창 주인 — 브라우저 기본 알림창 대신 운영사 로고가 있는 알림창을 띄운다(utils/alertBridge.js)
import AlertHost from './components/common/AlertHost';
import { setAlertDesignTemplate } from './utils/alertBridge';
import { LabelsProvider } from './contexts/LabelsContext';
import config from './config';
import TopNavBar from './components/layout/TopNavBar';
import Footer from './components/layout/Footer';
import PopupManager from './components/PopupManager';
// [쪽지/고객센터 분리] (2026-05-06) 미읽음 쪽지 강제 노출 모달.
//   gateNoticeUnread > 0 일 때 자동 노출 + 닫기 후 5초 자동 재노출 + 게임 게이트 트리거 수신.
import UnreadNoticeModal from './components/common/UnreadNoticeModal';
// [2026-08-07] 실시간 상담 채팅 위젯 — 우하단 플로팅. 노출은 서버 판정(마스터 ∧ 디자인 토글)
import ChatWidget from './components/common/ChatWidget';
import './styles/App.css';

// 핵심 페이지 - 즉시 로드 (초기 진입점)
import HomePage from './pages/HomePage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import RegisterPage from './pages/RegisterPage.jsx';
import SlotPage from './pages/SlotPage.jsx';
import CasinoPage from './pages/CasinoPage.jsx';
// [운영사 게임종류 토글] 미운영 게임종류 페이지 직접 접근 차단용 라우트 가드
import GameTypeGuard from './components/routing/GameTypeGuard.jsx';

// 스포츠 베팅 - 지연 로드
const SportsPage = lazy(() => import('./pages/SportsPage.jsx'));
// 2026-04-22: 경기결과 페이지 — 6탭(조합배팅/승무패/핸디캡/언오버/스페셜/라이브)
// WHY: /sports 와 별개 루트. 배당 데이터(진행 중)와 결과 데이터(종료)는 소스가 달라
//      화면 상태 관리가 분리되는 게 안전하다.
const SportsResultsPage = lazy(() => import('./pages/SportsResultsPage.jsx'));
// [2026-10-07 미니게임] 미니게임 목록(상단 메뉴 "미니게임") — 운영사가 켠 곳만 쓰는 게임종류라 지연 로드(안 쓰는 사이트의 첫 화면 용량에 넣지 않는다)
const MinigamePage = lazy(() => import('./pages/MinigamePage.jsx'));

// 마이페이지 - 코드 스플리팅으로 지연 로드 (초기 번들 크기 절감)
const DepositPage = lazy(() => import('./pages/DepositPage.jsx'));
const CryptoDepositPage = lazy(() => import('./pages/CryptoDepositPage.jsx'));
const WithdrawalPage = lazy(() => import('./pages/WithdrawalPage.jsx'));
const MyPageInfo = lazy(() => import('./pages/MyPageInfo.jsx'));
const CustomerServicePage = lazy(() => import('./pages/CustomerServicePage.jsx'));
// [쪽지/고객센터 분리] (2026-05-06) 고객센터 페이지 — CustomerServicePage thin wrapper.
const CustomerCenterPage = lazy(() => import('./pages/CustomerCenterPage.jsx'));
const TransactionHistoryPage = lazy(() => import('./pages/TransactionHistoryPage.jsx'));
const BettingHistoryPage = lazy(() => import('./pages/BettingHistoryPage.jsx'));
const EventPageAPI = lazy(() => import('./pages/EventPageAPI.jsx'));
const GuidePage = lazy(() => import('./pages/GuidePage.jsx'));
// 2026-04-20: 출석체크 페이지 (레퍼런스 UI — 달력·덧글·조건미달 모달)
const AttendancePage = lazy(() => import('./pages/AttendancePage.jsx'));

// 코드 스플리팅 - 지연 로드 (테스트/디버그 페이지만)
// DoriGamePage 제거됨 - 미사용
const GameplayPage = lazy(() => import('./pages/GameplayPage.jsx'));
const CasinoGameplayPage = lazy(() => import('./pages/CasinoGameplayPage.jsx'));
const MaintenanceTestPage = lazy(() => import('./pages/MaintenanceTestPage.jsx'));

// 로딩 컴포넌트
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    color: '#fff'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(255,255,255,0.3)',
        borderTop: '3px solid #fff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 15px'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div>로딩 중...</div>
    </div>
  </div>
);

function AppContent() {
  const location = useLocation();
  usePresencePageReport(location.pathname); // [2026-09-22] 화면 경로가 바뀔 때마다 서버에 현재 페이지 보고

  // [2026-09-29 운영자 지시 "페이지 이동 시 스크롤된 채 나오는 문제"] 화면 이동 시 스크롤 위치 — 디자인1(user-page-source App.js, 2026-08-05)과 같은 규칙.
  //   화면(주소의 경로)이 바뀌는 이동은 맨 위에서 시작하고, 뒤로/앞으로 이동은 그 화면을 떠날 때의 위치로 되돌린다.
  //   WHY: 이 사이트의 화면 전환은 문서를 새로 받지 않으므로 브라우저가 스크롤을 건드리지 않는다. 루미나에는 이 처리가 없어
  //        홈을 1,500px 내린 채 "로그인"을 누르면 로그인 화면이 336px 내려간 채(화면 최대치로 잘린 위치) 열렸다(ur3 실측).
  //        같은 화면 안에서 조건만 바뀌는 이동(주소의 물음표 뒤만 변경, 예: 스포츠 화면 탭)은 건드리지 않아 기존 동작을 유지한다.
  const navigationType = useNavigationType();
  const scrollMemoRef = React.useRef(new Map());   // 화면 이동 이력별 마지막 스크롤 위치
  const currentKeyRef = React.useRef(location.key);
  const prevPathRef = React.useRef(location.pathname);

  // 현재 화면의 스크롤 위치를 계속 기억해 둔다(이동 직후에 곧바로 꺼내 쓰기 위함)
  React.useEffect(() => {
    const remember = () => {
      scrollMemoRef.current.set(currentKeyRef.current, Math.round(window.scrollY));
    };
    window.addEventListener('scroll', remember, { passive: true });
    return () => window.removeEventListener('scroll', remember);
  }, []);

  React.useEffect(() => {
    const previousPath = prevPathRef.current;
    currentKeyRef.current = location.key;
    prevPathRef.current = location.pathname;

    // 뒤로/앞으로: 그 이력에 기억된 위치로 되돌린다(목록이 다시 그려질 때까지 최대 3초간 재시도)
    if (navigationType === 'POP') {
      const saved = scrollMemoRef.current.get(location.key);
      if (typeof saved !== 'number') return undefined;
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        const reachable = document.documentElement.scrollHeight - window.innerHeight;
        if (reachable >= saved || tries > 30) {
          window.scrollTo(0, saved);
          clearInterval(timer);
        }
      }, 100);
      return () => clearInterval(timer);
    }

    // 같은 화면 안에서 조건만 바뀐 이동이면 위치를 유지한다
    if (location.pathname === previousPath) return undefined;

    // 새 화면이면 그려진 뒤 맨 위로 이동
    requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, 0)));
    return undefined;
  }, [location.key, location.pathname, navigationType]);

  // 도메인별 테마 적용
  const { theme, settings, designTemplate, loading: themeLoading, error: themeError } = useDomainTheme();
  // [2026-09-27] 사이트 알림창(App 맨 바깥의 AlertHost)에 로고 정보를 넘긴다 — 그 창은 화면 분기와 무관하게 붙어 있어 디자인 정보를 안쪽에서 물려받지 못한다
  React.useEffect(() => { setAlertDesignTemplate(designTemplate); }, [designTemplate]);
  
  // 도메인 권한 체크
  const { hasPermission, permissionLoading, permissionError, permissionDetails } = useDomainPermission();
  
  // 점검모드 확인
  const [maintenanceMode, setMaintenanceMode] = React.useState(false);
  const [maintenanceSettings, setMaintenanceSettings] = React.useState(null);
  
  // 점검설정 로드 함수
  const loadMaintenanceSettings = React.useCallback(async () => {
    try {
      console.log('=== 점검모드 디버깅 ===');
      
      // API에서 점검 설정 로드
      const API_BASE_URL = config.API_URL || `http://${window.location.hostname}:5101/api`;
      const response = await fetch(`${API_BASE_URL}/maintenance-settings`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const settings = result.data;
          console.log('API에서 로드한 설정:', settings);
          console.log('target 값:', settings.target);
          setMaintenanceSettings(settings);
          
          // 점검 대상이 'member' 또는 'all'인 경우 점검모드 활성화
          if (settings.target === 'member' || settings.target === 'all') {
            console.log('점검모드 활성화!');
            setMaintenanceMode(true);
          } else {
            console.log('점검모드 비활성화 - target:', settings.target);
            setMaintenanceMode(false);
          }
        }
      } else {
        // API 실패시 localStorage에서 백업 로드
        console.log('API 실패, localStorage에서 백업 로드');
        const saved = localStorage.getItem('maintenanceSettings');
        
        if (saved) {
          const settings = JSON.parse(saved);
          console.log('localStorage에서 로드한 설정:', settings);
          setMaintenanceSettings(settings);
          
          if (settings.target === 'member' || settings.target === 'all') {
            console.log('점검모드 활성화! (localStorage)');
            setMaintenanceMode(true);
          } else {
            console.log('점검모드 비활성화 - target:', settings.target);
            setMaintenanceMode(false);
          }
        } else {
          console.log('점검설정 없음 - 점검모드 비활성화');
          setMaintenanceMode(false);
        }
      }
    } catch (error) {
      console.error('점검설정 로드 오류:', error);
      
      // 에러 발생시 localStorage에서 백업 로드
      try {
        const saved = localStorage.getItem('maintenanceSettings');
        if (saved) {
          const settings = JSON.parse(saved);
          setMaintenanceSettings(settings);
          if (settings.target === 'member' || settings.target === 'all') {
            setMaintenanceMode(true);
          }
        }
      } catch (backupError) {
        console.error('백업 로드도 실패:', backupError);
        setMaintenanceMode(false);
      }
    }
  }, []);

  React.useEffect(() => {
    // 초기 로드
    loadMaintenanceSettings();
    
    // localStorage 변경 감지 (다른 탭에서 변경될 때)
    const handleStorageChange = (e) => {
      if (e.key === 'maintenanceSettings') {
        console.log('점검설정이 다른 탭에서 변경됨');
        loadMaintenanceSettings();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadMaintenanceSettings]);
  
  // 로그인 페이지에서는 네비게이션과 푸터를 숨김
  const hideLayout = location.pathname === '/login' || location.pathname === '/register';

  // 테마 로딩 중 표시 - 3초 후에는 기본 페이지 표시
  const [forceShow, setForceShow] = React.useState(false);
  
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (themeLoading) {
        console.warn('테마 로딩 타임아웃 - 기본 페이지 표시');
        setForceShow(true);
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [themeLoading]);
  
  if (themeLoading && !forceShow) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>테마를 불러오는 중...</div>
      </div>
    );
  }

  // 도메인 권한 체크 중
  if (permissionLoading) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>도메인 접근 권한을 확인하는 중...</div>
      </div>
    );
  }

  // 도메인 권한 없음 - 로그인 페이지로 리다이렉트
  if (!hasPermission && !permissionLoading) {
    // 토큰이 있는데 권한이 없으면 토큰 삭제
    const token = localStorage.getItem('token');
    if (token) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.clear();
    }
    // 로그인 페이지로 리다이렉트 (도메인 접근 차단 화면 표시하지 않음)
    return <Navigate to="/login" replace />;
  }
  
  // 아래 코드는 실행되지 않음 (주석 처리)
  /*
  if (!hasPermission) {
    return (
      <div className="App" style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '20px' }}>도메인 접근 차단</h2>
          <p style={{ marginBottom: '15px', color: '#666', fontSize: '18px', fontWeight: 'bold' }}>
            {permissionError || '이 도메인에 대한 접근 권한이 없습니다.'}
          </p>
          {permissionDetails?.blockedBy && (
            <p style={{ fontSize: '14px', color: '#999' }}>
              차단 사유: {permissionDetails.blockedBy}님이 도메인을 선택하지 않았습니다.
            </p>
          )}
          {permissionDetails?.reason && (
            <div style={{ 
              marginTop: '20px', 
              padding: '15px', 
              backgroundColor: '#fff3e0',
              borderRadius: '5px',
              border: '1px solid #ffb74d'
            }}>
              <p style={{ fontSize: '14px', color: '#f57c00', margin: 0 }}>
                {permissionDetails.reason}
              </p>
            </div>
          )}
          <div style={{ marginTop: '30px' }}>
            <p style={{ fontSize: '16px', color: '#555', marginBottom: '10px' }}>
              관리자에게 문의하여 도메인 접근 권한을 요청하세요.
            </p>
            <p style={{ fontSize: '14px', color: '#999' }}>
              권한이 부여되면 이 페이지가 자동으로 활성화됩니다.
            </p>
          </div>
        </div>
      </div>
    );
  }
  */

  // 점검모드일 때 점검 페이지 표시
  if (maintenanceMode && maintenanceSettings) {
    return (
      <div 
        className="App" 
        style={{
          width: '100%',
          height: '100vh',
          backgroundColor: maintenanceSettings.backgroundColor,
          backgroundImage: maintenanceSettings.backgroundImage ? `url(${maintenanceSettings.backgroundImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* 메인 로고 */}
        {maintenanceSettings.mainLogo && (
          <div
            style={{
              position: 'absolute',
              left: `${maintenanceSettings.mainLogoPosition.x}%`,
              top: `${maintenanceSettings.mainLogoPosition.y}%`,
              transform: 'translate(-50%, -50%)',
              width: maintenanceSettings.mainLogoSize,
              height: 'auto'
            }}
          >
            <img 
              src={maintenanceSettings.mainLogo} 
              alt="Logo" 
              style={{ width: '100%', height: 'auto' }}
            />
          </div>
        )}

        {/* 메인 텍스트 */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 20px'
          }}
        >
          {/* DOMPurify로 HTML 정제 후 렌더링 - XSS 방지 */}
          <div
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(maintenanceSettings.mainText) }}
            style={{
              maxWidth: '800px',
              textAlign: 'center'
            }}
          />
        </div>

        {/* 푸터 */}
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }}
        >
          {maintenanceSettings.footerLogo && (
            <div style={{ marginBottom: '10px' }}>
              <img 
                src={maintenanceSettings.footerLogo} 
                alt="Footer Logo" 
                style={{ height: '40px' }}
              />
            </div>
          )}
          
          {/* SNS 아이콘 */}
          <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
            {maintenanceSettings.socialLinks.map((link, index) => (
              link.icon && link.url && (
                <a key={index} href={link.url} target="_blank" rel="noopener noreferrer">
                  <img 
                    src={link.icon} 
                    alt={link.platform} 
                    style={{ width: '30px', height: '30px' }}
                  />
                </a>
              )
            ))}
          </div>

          <div style={{ color: '#ccc', fontSize: '14px' }}>
            {maintenanceSettings.footerText}
          </div>
        </div>
      </div>
    );
  }

  return (
    <DesignTemplateProvider value={designTemplate}>
      <LabelsProvider>
      <div className="App">
        {!hideLayout && (
          <div id="topbarnav-container">
            <TopNavBar />
          </div>
        )}
        {/* WHY: 비로그인(로그인/회원가입) 화면은 풀스크린(100vh) 디자인을 위해 minHeight 유지.
            그 외 페이지에서 calc(100vh - 110px) 강제는 콘텐츠가 짧을 때 main 안에 빈 영역을
            남겨 검은 공백(body 배경) 으로 노출하는 회귀를 유발했으므로 자연 콘텐츠 흐름으로 둔다.
            짧은 페이지 footer 위치는 footer 자체 디자인에 위임. */}
        <main style={{ minHeight: hideLayout ? '100vh' : 'auto' }}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* 핵심 페이지 - 즉시 로드 */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              {/* [운영사 게임종류 토글] 미운영 게임종류 페이지는 홈으로 리다이렉트 */}
              <Route path="/slot" element={<GameTypeGuard gameType="slot"><SlotPage /></GameTypeGuard>} />
              <Route path="/casino" element={<GameTypeGuard gameType="casino"><CasinoPage /></GameTypeGuard>} />
              <Route path="/live-casino" element={<GameTypeGuard gameType="casino"><CasinoPage /></GameTypeGuard>} />
              {/* 스포츠 베팅 */}
              <Route path="/sports" element={<GameTypeGuard gameType="sports"><Suspense fallback={<PageLoader />}><SportsPage /></Suspense></GameTypeGuard>} />
              {/* 경기결과 — 종료 경기 목록. 인증 필요(비로그인 열람 비허용, 내부 authMiddleware) */}
              <Route path="/sports/results" element={<GameTypeGuard gameType="sports"><Suspense fallback={<PageLoader />}><SportsResultsPage /></Suspense></GameTypeGuard>} />
              {/* [2026-10-07 미니게임] 미니게임 목록 — 다른 게임종류와 같은 가드(미운영이면 홈으로). 목록은 비로그인도 본다(카지노와 같다) */}
              <Route path="/minigame" element={<GameTypeGuard gameType="minigame"><Suspense fallback={<PageLoader />}><MinigamePage /></Suspense></GameTypeGuard>} />
              {/* 마이페이지 - 지연 로드 */}
              <Route path="/mypage/deposit" element={<DepositPage />} />
              {/* [2026-11-03] 코인입금 재개(운영자 지시 "usdt는 다시 쓸 수 있게 해야 한다") — 2026-05-07 에 막은 경로를 되살렸다. 메뉴는 신청할 수 있는 운영사에만 보인다. */}
              <Route path="/mypage/crypto-deposit" element={<CryptoDepositPage />} />
              <Route path="/mypage/withdrawal" element={<WithdrawalPage key="bank" />} />
              {/* [2026-11-03] 코인출금 — 같은 화면이므로 key 로 상태를 나눈다(입력값이 다른 쪽으로 넘어가지 않게) */}
              <Route path="/mypage/crypto-withdrawal" element={<WithdrawalPage key="coin" coin />} />
              <Route path="/mypage/info" element={<MyPageInfo />} />
              {/* [쪽지/고객센터 분리] (2026-05-06)
                  - /mypage/customer (기존)        : 쪽지 화면 (category='notice')
                  - /mypage/customer-center (신규) : 고객센터 화면 (category='inquiry')
                  같은 CustomerServicePage 컴포넌트 재사용. CustomerCenterPage 는 thin wrapper.
              */}
              <Route path="/mypage/customer" element={<CustomerServicePage category="notice" />} />
              <Route path="/mypage/customer-center" element={<CustomerCenterPage />} />
              <Route path="/mypage/transaction-history" element={<TransactionHistoryPage />} />
              <Route path="/mypage/guide" element={<GuidePage />} />
              <Route path="/mypage/betting-list" element={<BettingHistoryPage />} />
              <Route path="/mypage/event" element={<EventPageAPI />} />
              {/* 2026-04-20: 출석체크 페이지 — TopNavBar "출석체크" 링크가 여기로 연결 */}
              <Route path="/mypage/attendance" element={<AttendancePage />} />
              {/* 게임 페이지 - 지연 로드 */}
              {/* DoriGamePage 제거됨 - 미사용 */}
              <Route path="/gameplay/:gameId" element={<GameplayPage />} />
              <Route path="/casino-gameplay/:gameId" element={<CasinoGameplayPage />} />
              {/* 기타 페이지 - 지연 로드 */}
              {/* [2026-10-16] /test-macaw-logo(개발용 로고 시험 화면 — 연결 메뉴 없음) 경로는 지웠다 */}
              <Route path="/invite" element={<div style={{ padding: '20px', color: 'white' }}>친구초대</div>} />
              {/* [2026-10-16] 개발용 시험 화면 경로(/test-theme·/gradient-demo·/image-debug — 연결 메뉴 없음, 디자인 설정 원문 등 시험 표시)는 지웠다 */}
              <Route path="/board/notices" element={<Navigate to="/mypage/event?tab=notice" replace />} />
              <Route path="/board/notices/:id" element={<Navigate to="/mypage/event?tab=notice" replace />} />
              {/* 점검 테스트 페이지 - 개발환경에서만 접근 허용 */}
              {process.env.NODE_ENV === 'development' && <Route path="/maintenance-test" element={<MaintenanceTestPage />} />}
            </Routes>
          </Suspense>
        </main>
        {!hideLayout && <Footer />}
        <PopupManager />
        {/* [쪽지/고객센터 분리] 글로벌 마운트 — 모든 라우트에서 미읽음 쪽지 모달 강제 노출 가능. */}
        <UnreadNoticeModal />
        {/* [2026-08-07] 상담 채팅 — 마스터·디자인 토글이 켜진 도메인에서만 내부적으로 표시 */}
        <ChatWidget />
      </div>
      </LabelsProvider>
    </DesignTemplateProvider>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
        {/* [2026-09-27] 사이트 알림창 — 앱 맨 바깥에 한 번. 로딩·권한 확인·로그인 화면·점검 화면에서도 알림이 뜬다 */}
        <AlertHost />
      </AuthProvider>
    </Router>
  );
}

export default App;